import { useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  CircleNotch, DownloadSimple, UploadSimple, FileXls, FileCsv,
  CheckCircle, WarningCircle, XCircle, Copy, ArrowCounterClockwise,
} from "@phosphor-icons/react";

const STATUS_META = {
  valid: { label: "Valid", cls: "bg-[#65AB15]/10 text-[#4c840f]", Icon: CheckCircle },
  warning: { label: "Warning", cls: "bg-amber-100 text-amber-800", Icon: WarningCircle },
  needs_review: { label: "Needs review", cls: "bg-amber-100 text-amber-900", Icon: WarningCircle },
  duplicate: { label: "Possible duplicate", cls: "bg-orange-100 text-orange-800", Icon: Copy },
  error: { label: "Error", cls: "bg-red-100 text-red-700", Icon: XCircle },
};

function downloadBlob(content, filename, type) {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([content], { type }));
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

function rowAddress(f) {
  return [f.street_address, f.address_line2, f.city, f.state, f.zip_code].filter(Boolean).join(", ") || "—";
}

function errorReportCsv(rows) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [["Row", "Property Name", "Status", "Messages"].map(esc).join(",")];
  rows.forEach((r) => {
    const msgs = [...(r.errors || []), ...(r.warnings || []),
      ...(r.invalid_values || []).map((iv) => `${iv.column} '${iv.value}' is not a recognized value`),
      r.duplicate?.existing_property_name ? `Possible duplicate of '${r.duplicate.existing_property_name}'` : null,
      r.duplicate?.in_file_row ? `Duplicate of row ${r.duplicate.in_file_row} in this file` : null,
    ].filter(Boolean).join(" | ");
    lines.push([r.row_number, r.fields?.name || "", STATUS_META[r.status]?.label || r.status, msgs].map(esc).join(","));
  });
  downloadBlob("\ufeff" + lines.join("\n"), "property_import_issues.csv", "text/csv");
}

export default function BulkImportDialog({ open, prospect, onClose, onDone }) {
  const [step, setStep] = useState("start");
  const [busy, setBusy] = useState(false);
  const [batch, setBatch] = useState(null);
  const [included, setIncluded] = useState({});
  const [approved, setApproved] = useState({});
  const [approveAll, setApproveAll] = useState(false);
  const [approveBlank, setApproveBlank] = useState({});
  const [unknownHeadings, setUnknownHeadings] = useState(null); // {filename, b64, headings}
  const [results, setResults] = useState(null);
  const fileRef = useRef(null);

  const downloadTemplate = async (fmt) => {
    try {
      const res = await api.get(`/prospects/${prospect.id}/property-import/template?format=${fmt}`, { responseType: "blob" });
      downloadBlob(res.data, `property_import_template.${fmt}`, res.data.type);
    } catch (e) {
      toast.error("Template could not be downloaded — please try again");
    }
  };

  const runValidate = async (filename, b64, ignoreUnknown) => {
    const { data } = await api.post(`/prospects/${prospect.id}/property-import/validate`,
      { filename, content_base64: b64, ignore_unknown_headings: ignoreUnknown });
    if (data.needs_confirmation) {
      setUnknownHeadings({ filename, b64, headings: data.unknown_headings });
      return;
    }
    setUnknownHeadings(null);
    setBatch(data);
    const inc = {};
    data.rows.forEach((r) => { inc[r.row_number] = r.status === "valid" || r.status === "warning"; });
    setIncluded(inc);
    setApproved({});
    setApproveBlank({});
    setApproveAll(false);
    setStep("preview");
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const b64 = await new Promise((resolve, reject) => {
        const rd = new FileReader();
        rd.onload = () => resolve(String(rd.result).split(",")[1]);
        rd.onerror = reject;
        rd.readAsDataURL(file);
      });
      await runValidate(file.name, b64, false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const importableRows = (batch?.rows || []).filter((r) =>
    included[r.row_number] && (r.status === "valid" || r.status === "warning"
      || (r.status === "needs_review" && approveBlank[r.row_number])));
  const issueRows = (batch?.rows || []).filter((r) => r.status !== "valid");

  const commit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `/prospects/${prospect.id}/property-import/${batch.id}/commit`, {
          include_rows: importableRows.map((r) => r.row_number),
          approve_all_contacts: approveAll,
          approve_contact_rows: Object.keys(approved).filter((k) => approved[k]).map(Number),
          approve_blank_field_rows: Object.keys(approveBlank).filter((k) => approveBlank[k]).map(Number),
        });
      setResults(data);
      setStep("results");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => { setStep("start"); setBatch(null); setResults(null); };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto rounded-sm sm:max-w-4xl" data-testid="bulk-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-[#032852]">Bulk Import Properties</DialogTitle>
          <DialogDescription>
            All imported locations are linked to <span className="font-semibold text-[#032852]">{prospect.organization_name}</span>.
            Nothing is written until you confirm the preview.
          </DialogDescription>
        </DialogHeader>

        {step === "start" && (
          <div className="space-y-5">
            {unknownHeadings && (
              <div className="rounded-sm border border-amber-300 bg-amber-50 p-4" data-testid="unknown-headings-panel">
                <p className="text-xs font-bold uppercase tracking-wide text-amber-900">Unrecognized column headings</p>
                <p className="mt-1 text-xs text-amber-900">
                  {unknownHeadings.headings.map((h) => `"${h}"`).join(", ")} — these columns are not part of the
                  template and will NOT be imported. Correct the headings and re-upload, or confirm to ignore them.
                </p>
                <div className="mt-3 flex gap-2">
                  <Button type="button" size="sm" disabled={busy} className="rounded-sm bg-[#569A1F] font-semibold text-white hover:bg-[#457d18]"
                    onClick={async () => {
                      setBusy(true);
                      try { await runValidate(unknownHeadings.filename, unknownHeadings.b64, true); }
                      catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                      finally { setBusy(false); }
                    }} data-testid="ignore-unknown-headings-btn">
                    Ignore these columns and continue
                  </Button>
                  <Button type="button" size="sm" variant="outline" className="rounded-sm" onClick={() => setUnknownHeadings(null)}>
                    I will correct the file
                  </Button>
                </div>
              </div>
            )}
            <div className="rounded-sm border border-[#E4E4E7] bg-[#FAFAFA] p-4">
              <p className="text-xs font-bold uppercase tracking-wide text-[#52525B]">1 — Download the template</p>
              <p className="mt-1 text-xs text-[#6D6D70]">Property Name is the only required column. The Excel version includes an Instructions sheet with accepted values.</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button type="button" variant="outline" className="rounded-sm" onClick={() => downloadTemplate("xlsx")} data-testid="download-template-xlsx">
                  <FileXls size={16} className="text-[#1D6F42]" /> Excel template
                </Button>
                <Button type="button" variant="outline" className="rounded-sm" onClick={() => downloadTemplate("csv")} data-testid="download-template-csv">
                  <FileCsv size={16} className="text-[#032852]" /> CSV template
                </Button>
              </div>
            </div>
            <div className="rounded-sm border border-[#E4E4E7] bg-[#FAFAFA] p-4">
              <p className="text-xs font-bold uppercase tracking-wide text-[#52525B]">2 — Upload the completed file</p>
              <p className="mt-1 text-xs text-[#6D6D70]">.xlsx or .csv — you will review every row before anything is imported.</p>
              <input ref={fileRef} type="file" accept=".xlsx,.csv" className="hidden" onChange={onFile} data-testid="bulk-import-file-input" />
              <Button type="button" disabled={busy} className="mt-3 rounded-sm bg-[#569A1F] font-semibold text-white hover:bg-[#457d18]"
                onClick={() => fileRef.current?.click()} data-testid="bulk-import-upload-btn">
                {busy ? <CircleNotch size={18} className="animate-spin" /> : <UploadSimple size={16} weight="bold" />} Upload file
              </Button>
            </div>
          </div>
        )}

        {step === "preview" && batch && (
          <div className="space-y-4">
            <div className="rounded-sm border border-[#032852]/20 bg-[#032852]/5 px-3 py-2 text-sm font-semibold text-[#032852]" data-testid="import-destination">
              Destination prospect: {batch.prospect_name || prospect.organization_name}
              <span className="ml-2 font-normal text-[#52525B]">— spreadsheet content can never redirect this import to a different prospect.</span>
            </div>
            <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="bulk-import-counts">
              <span className="rounded-sm bg-[#65AB15]/10 px-2 py-1 font-semibold text-[#4c840f]">{batch.counts.valid} valid</span>
              <span className="rounded-sm bg-amber-100 px-2 py-1 font-semibold text-amber-800">{batch.counts.warnings} warnings</span>
              <span className="rounded-sm bg-amber-100 px-2 py-1 font-semibold text-amber-900">{batch.counts.needs_review || 0} need review</span>
              <span className="rounded-sm bg-orange-100 px-2 py-1 font-semibold text-orange-800">{batch.counts.duplicates} possible duplicates</span>
              <span className="rounded-sm bg-red-100 px-2 py-1 font-semibold text-red-700">{batch.counts.errors} errors</span>
              <span className="ml-auto text-[#6D6D70]">{batch.filename}</span>
            </div>
            {batch.counts.proposed_contacts > 0 && (
              <label className="flex items-center gap-2 rounded-sm border border-[#E4E4E7] bg-[#FAFAFA] px-3 py-2 text-xs font-semibold text-[#032852]">
                <Checkbox checked={approveAll} onCheckedChange={(v) => setApproveAll(Boolean(v))} data-testid="approve-all-contacts" />
                Approve all {batch.counts.proposed_contacts} proposed new contact{batch.counts.proposed_contacts === 1 ? "" : "s"}
                <span className="font-normal text-[#6D6D70]">— unapproved contacts are never created; the property imports without one</span>
              </label>
            )}
            <div className="max-h-[42vh] overflow-auto rounded-sm border border-[#E4E4E7]">
              <table className="w-full text-left text-xs">
                <thead className="sticky top-0 bg-[#F4F4F5] text-[10px] font-bold uppercase tracking-wide text-[#52525B]">
                  <tr>
                    <th className="px-2 py-2">Include</th>
                    <th className="px-2 py-2">Row</th>
                    <th className="px-2 py-2">Property</th>
                    <th className="px-2 py-2">Address</th>
                    <th className="px-2 py-2">Contact</th>
                    <th className="px-2 py-2">Status</th>
                    <th className="px-2 py-2">Messages</th>
                  </tr>
                </thead>
                <tbody>
                  {batch.rows.map((r) => {
                    const meta = STATUS_META[r.status];
                    const blocked = r.status === "error" || r.status === "duplicate"
                      || (r.status === "needs_review" && !approveBlank[r.row_number]);
                    return (
                      <tr key={r.row_number} className="border-t border-[#F4F4F5] align-top" data-testid={`import-row-${r.row_number}`}>
                        <td className="px-2 py-2">
                          <Checkbox checked={!blocked && !!included[r.row_number]} disabled={blocked}
                            onCheckedChange={(v) => setIncluded((p) => ({ ...p, [r.row_number]: Boolean(v) }))}
                            data-testid={`include-row-${r.row_number}`} />
                        </td>
                        <td className="px-2 py-2 text-[#6D6D70]">{r.row_number}</td>
                        <td className="px-2 py-2 font-semibold text-[#09090B]">{r.fields.name || "—"}</td>
                        <td className="px-2 py-2 text-[#52525B]">{rowAddress(r.fields)}</td>
                        <td className="px-2 py-2">
                          {!r.contact && <span className="text-[#6D6D70]">—</span>}
                          {r.contact?.kind === "matched" && (
                            <span className="text-[#4c840f]">Matched: {r.contact.name}</span>
                          )}
                          {r.contact?.kind === "proposed" && (
                            <label className="flex items-center gap-1.5 text-amber-800">
                              <Checkbox checked={approveAll || !!approved[r.row_number]} disabled={approveAll}
                                onCheckedChange={(v) => setApproved((p) => ({ ...p, [r.row_number]: Boolean(v) }))}
                                data-testid={`approve-contact-${r.row_number}`} />
                              New contact proposed: {r.contact.name}
                            </label>
                          )}
                        </td>
                        <td className="px-2 py-2">
                          <span className={`inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 font-semibold ${meta.cls}`}>
                            <meta.Icon size={12} weight="bold" /> {meta.label}
                          </span>
                        </td>
                        <td className="px-2 py-2 text-[#6D6D70]">
                          {[...(r.errors || []), ...(r.warnings || [])].map((m, i) => <p key={i}>{m}</p>)}
                          {(r.invalid_values || []).map((iv, i) => (
                            <p key={`iv-${i}`} className="text-amber-900">
                              {iv.column} <span className="font-semibold">"{iv.value}"</span> is not a recognized value — correct the file, exclude the row, or approve below.
                            </p>
                          ))}
                          {r.status === "needs_review" && (
                            <label className="mt-1 flex items-center gap-1.5 font-semibold text-[#032852]">
                              <Checkbox checked={!!approveBlank[r.row_number]}
                                onCheckedChange={(v) => {
                                  setApproveBlank((p) => ({ ...p, [r.row_number]: Boolean(v) }));
                                  setIncluded((p) => ({ ...p, [r.row_number]: Boolean(v) }));
                                }}
                                data-testid={`approve-blank-${r.row_number}`} />
                              Import with the unrecognized field(s) left blank
                            </label>
                          )}
                          {r.duplicate?.existing_property_name && (
                            <p>Matches existing property <span className="font-semibold text-[#032852]">{r.duplicate.existing_property_name}</span> — review it on the Properties tab. It will be skipped.</p>
                          )}
                          {r.duplicate?.in_file_row && <p>Duplicate of row {r.duplicate.in_file_row} in this file — it will be skipped.</p>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <DialogFooter className="flex-wrap gap-2">
              {issueRows.length > 0 && (
                <Button type="button" variant="outline" className="rounded-sm" onClick={() => errorReportCsv(issueRows)} data-testid="download-error-report">
                  <DownloadSimple size={16} /> Error report
                </Button>
              )}
              <Button type="button" variant="outline" className="rounded-sm" onClick={reset} data-testid="reupload-btn">
                <ArrowCounterClockwise size={16} /> Re-upload corrected file
              </Button>
              <Button type="button" variant="outline" className="rounded-sm" onClick={onClose}>Cancel</Button>
              <Button type="button" disabled={busy || importableRows.length === 0} onClick={commit}
                className="rounded-sm bg-[#569A1F] font-semibold text-white hover:bg-[#457d18]" data-testid="confirm-import-btn">
                {busy ? <CircleNotch size={18} className="animate-spin" /> : null}
                Import {importableRows.length} propert{importableRows.length === 1 ? "y" : "ies"}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === "results" && results && (
          <div className="space-y-4" data-testid="bulk-import-results">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
              {[["Processed", results.summary.total_rows], ["Created", results.summary.created],
                ["Duplicates skipped", results.summary.skipped_duplicates],
                ["Rejected", results.summary.rejected], ["Excluded", results.summary.excluded]].map(([l, v]) => (
                <div key={l} className="rounded-sm border border-[#E4E4E7] bg-white p-3 text-center">
                  <p className="text-xl font-bold text-[#032852]">{v}</p>
                  <p className="text-[10px] font-bold uppercase tracking-wide text-[#6D6D70]">{l}</p>
                </div>
              ))}
            </div>
            {results.summary.contacts_created > 0 && (
              <p className="text-xs text-[#4c840f]">{results.summary.contacts_created} new contact(s) created with your approval.</p>
            )}
            <div className="max-h-[36vh] overflow-auto rounded-sm border border-[#E4E4E7]">
              <table className="w-full text-left text-xs">
                <thead className="sticky top-0 bg-[#F4F4F5] text-[10px] font-bold uppercase tracking-wide text-[#52525B]">
                  <tr><th className="px-2 py-2">Row</th><th className="px-2 py-2">Property</th><th className="px-2 py-2">Outcome</th><th className="px-2 py-2">Detail</th></tr>
                </thead>
                <tbody>
                  {results.results.map((r) => (
                    <tr key={r.row} className="border-t border-[#F4F4F5]">
                      <td className="px-2 py-1.5 text-[#6D6D70]">{r.row}</td>
                      <td className="px-2 py-1.5 font-semibold">{r.name}</td>
                      <td className="px-2 py-1.5">
                        <span className={`rounded-sm px-1.5 py-0.5 font-semibold ${
                          r.outcome === "created" ? "bg-[#65AB15]/10 text-[#4c840f]"
                            : r.outcome === "skipped_duplicate" ? "bg-orange-100 text-orange-800"
                              : r.outcome === "excluded" ? "bg-[#F4F4F5] text-[#52525B]" : "bg-red-100 text-red-700"}`}>
                          {r.outcome.replace(/_/g, " ")}
                        </span>
                      </td>
                      <td className="px-2 py-1.5 text-[#6D6D70]">{r.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <DialogFooter>
              {results.results.some((r) => r.outcome === "rejected" || r.outcome === "failed") && (
                <Button type="button" variant="outline" className="rounded-sm"
                  onClick={() => errorReportCsv((batch?.rows || []).filter((r) => r.status === "error"))}>
                  <DownloadSimple size={16} /> Error report
                </Button>
              )}
              <Button type="button" className="rounded-sm bg-[#032852] font-semibold text-white hover:bg-[#032852]/90"
                onClick={onClose} data-testid="view-imported-btn">
                View imported properties
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
